import { useRef, useState, useEffect, useCallback, Suspense } from 'react';
import SceneErrorBoundary from './SceneErrorBoundary';

/* ─────────────────────────────────────────────────────────────────────────
   Mounts a heavy lazy 3D <Canvas> only near the viewport and releases its
   WebGL context once far away — keeping ~one live context at a time (multiple
   simultaneous WebGL contexts are a scroll-perf killer).

   Robust against the fast-scroll race that made scenes "not load on the first
   try": the previous version unmounted the instant the section left the zone,
   so a fast scroll could tear a scene down mid-chunk-load, leaving it blank
   until re-approached. Now:
     • Once a scene starts loading it is NEVER unmounted until it reports ready
       (onReady) — a fast scroll-through completes the load off-screen instead
       of cancelling it. Only a ready scene is released when scrolled far away.
     • A visible loading state (not blank) shows while the chunk/GL initialise.
     • Errors are caught, logged, shown as a fallback, and retried on the next
       approach (fresh key).

   Usage: children is a render fn given `onReady` to wire to the scene's
   Canvas onCreated:  <SceneMount>{(onReady) => <Hero3D onReady={onReady} />}</SceneMount>
──────────────────────────────────────────────────────────────────────── */
export default function SceneMount({
  children,
  rootMargin = '1600px 0px 1600px 0px',
  style,
  className,
  label = 'scene',
  loader = null,
  errorFallback = null,
}) {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [ready, setReady] = useState(false);
  const [attempt, setAttempt] = useState(0);   // bump to remount after an error
  const mountT = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setNear(e.isIntersecting), { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  // Mount when near. When scrolled away, only unmount if the scene finished
  // loading (ready) — never cancel an in-flight first load.
  useEffect(() => {
    if (near) setMounted(true);
    else if (ready) setMounted(false);
  }, [near, ready]);

  // On unmount, clear ready so the NEXT mount's load is protected again.
  useEffect(() => { if (!mounted) setReady(false); }, [mounted]);

  // Diagnostic: time from "start mounting" (chunk load + WebGL init begins) to
  // "scene rendered" (Canvas onCreated). Reveals whether the multi-second delay
  // users report is network (chunk download) or compute (shader compile).
  useEffect(() => { if (mounted && !ready) mountT.current = performance.now(); }, [mounted, ready]);

  const onReady = useCallback(() => {
    if (mountT.current) console.log(`[SceneMount:${label}] mount→visible ${Math.round(performance.now() - mountT.current)}ms`);
    setReady(true);
  }, [label]);
  const onError = useCallback(() => {
    // Let it retry from scratch the next time the section is approached.
    setReady(false);
    setMounted(false);
    setAttempt(a => a + 1);
  }, []);

  return (
    <div ref={ref} className={className} style={{ position: 'relative', ...style }}>
      {mounted ? (
        <SceneErrorBoundary key={attempt} label={label} onError={onError} fallback={errorFallback}>
          {/* Suspense fallback is null; the veil below covers BOTH the chunk
              load and the gap until the GL scene reports ready (no blank). */}
          <Suspense fallback={null}>
            {children(onReady)}
          </Suspense>
        </SceneErrorBoundary>
      ) : null}
      {mounted && !ready && (loader ?? <SceneLoader />)}
    </div>
  );
}

// Subtle, brand-neutral loading veil — a slow pulsing dot. Absolutely
// positioned so it overlays the scene box without shifting layout.
function SceneLoader() {
  return (
    <div aria-hidden="true" data-scene-loader style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4C82F7', boxShadow: '0 0 14px 3px rgba(76,130,247,0.6)', animation: 'scenePulse 1.1s ease-in-out infinite' }} />
      <style>{'@keyframes scenePulse{0%,100%{opacity:.25;transform:scale(.8)}50%{opacity:1;transform:scale(1.15)}}'}</style>
    </div>
  );
}
